import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { FormArray, FormControl, FormGroup, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngneat/elf';
import { UIEntitiesRef, addEntities, selectEntity, updateEntities } from '@ngneat/elf-entities';
import { FormType, createForm } from 'ngx-sub-form';
import { Subject } from 'rxjs';
import { Choke, Chokes, ChokesUi, FlowCoefficientSchedule } from 'src/app/perical/models/chokes.model';
import { LinearInterpSingleVal } from 'src/app/perivis/shared/helpers/linear-interp-single.helper';
import {
  createChokeCvScheduleColumns,
  createChokeCvScheduleRow,
  ensureTrailingInputRow,
  extractChokeCvScheduleRows,
  populateChokeCvScheduleFormArray
} from './helpers/choke-cv-schedule.helper';

@Component({
    selector: 'app-choke-data',
    templateUrl: './choke-data.component.html',
    standalone: false
})
export class ChokeDataComponent implements OnChanges {
  public control(name: string): UntypedFormControl {
    return this.form.formGroup.get(name) as UntypedFormControl;
  }

  public chokeData: Chokes;
  public flowCoefficientSchedule: FlowCoefficientSchedule[];
  public plot = {
    data: [],
    layout: {},
    config: {}
  };
  public xAxisTitle = '% Open or Travel';
  public yAxisTitle = 'Flow Coefficient (Cv)';
  public plotName = 'flowCoefficientSchedule';
  public downloadPlotName = 'flow_coefficient_schedule';
  public interpolatedCv: number;

  public readonly cvScheduleColumns = createChokeCvScheduleColumns();
  public cvScheduleRows = new FormArray<FormGroup>([]);
  public createChokeCvScheduleRow = createChokeCvScheduleRow;

  @Input()
  public chokesStore: Store;
  public chokesUiEntity$: ChokesUi;

  private input$ = new Subject<Choke | null | undefined>();
  private _choke: Choke;
  @Input() set choke(choke: Choke | undefined) {
    this.input$.next(choke);
    this._choke = choke;
  }

  get choke(): Choke {
    return this._choke;
  }

  private disabled$ = new Subject<boolean>();
  @Input() set disabled(value: boolean | undefined) {
    this.disabled$.next(!!value);
  }

  @Output() chokeUpdate = new EventEmitter<Choke>();

  ngOnChanges(e: any): void {
    if (e.choke?.currentValue) {
      const schedule = e.choke.currentValue.flowCoefficientSchedule || [];
      const currentRows = extractChokeCvScheduleRows(this.cvScheduleRows);
      const isSame = this.cvScheduleRows.length > 0 && JSON.stringify(schedule) === JSON.stringify(currentRows);
      if (!isSame) {
        this.flowCoefficientSchedule = schedule.length > 0 ? schedule : [{ percentOpen: 0, flowCoefficient: 0 }];
        populateChokeCvScheduleFormArray(this.cvScheduleRows, this.flowCoefficientSchedule);
      }
    }
    this.plotEnvelope();

    if(this.chokesStore && e.choke?.currentValue) {
      this.chokesStore.pipe(selectEntity(e.choke.currentValue.name, { ref: UIEntitiesRef})).subscribe(chokeUi => {
        this.chokesUiEntity$ = chokeUi;
        if (!this.chokesUiEntity$ && e.choke.currentValue.name) {
          this.chokesStore.update(addEntities( {id: e.choke.currentValue.name, selectedResultsDisplay: false}, { ref: UIEntitiesRef }));
          this.chokesStore.pipe(selectEntity(e.choke.currentValue.name, { ref: UIEntitiesRef})).subscribe(chokeUI => this.chokesUiEntity$ = chokeUI);
        }
      })
    }
  }

  public tablePlotToggle(event: any) {
    this.chokesStore.update(updateEntities(this.choke.name, (choke) => ({...choke, selectedResultsDisplay: event.checked}), { ref: UIEntitiesRef }));
  }

  public onCvScheduleRowDeleted(index: number): void {
    if (index >= 0 && index < this.cvScheduleRows.length) {
      this.cvScheduleRows.removeAt(index);
    }
    this.onCvScheduleGridChange();
  }

  public onCvScheduleGridChange(): void {
    ensureTrailingInputRow(this.cvScheduleRows);
    const rawRows = extractChokeCvScheduleRows(this.cvScheduleRows);
    this.flowCoefficientSchedule = rawRows;
    this.form.formGroup.value['flowCoefficientSchedule'] = rawRows;
    this.plotEnvelope();
    this.chokeUpdate.emit(this.form.formGroup.value);
  }

  private plotEnvelope() {
    this.plot.data = [{
      y: this.flowCoefficientSchedule?.map(x => x.flowCoefficient),
      x: this.flowCoefficientSchedule?.map(x => x.percentOpen),
      showlegend: false
    }];
  }

  public form = createForm<Choke>(this, {
    formType: FormType.ROOT,
    disabled$: this.disabled$,
    input$: this.input$,
    output$: this.chokeUpdate,
    formControls: {
      name: new FormControl('', { updateOn: 'blur', validators: [Validators.required]}),
      criticalLiquidFlowFactor: new FormControl(null, { updateOn: 'blur', validators: [Validators.required, Validators.min(0), Validators.max(1)]}),
      criticalVaporFlowFactor: new FormControl(null, { updateOn: 'blur', validators: [Validators.required, Validators.min(0), Validators.max(1)]}),
      pipingGeometryFactor: new FormControl(null, { updateOn: 'blur', validators: [Validators.required]}),
      flowCoefficientSchedule: new FormControl(null, [Validators.required]),

    },
    fromFormGroup: (formValue: Choke | null): Choke => {
      const { ...commonValues } = formValue;

      return {
        ...commonValues
      }
    }
  });

  public interpolateCv(event: any) {
    const value = event['value'];
    const flowCoefficientSchedule = this.form.formGroup.value['flowCoefficientSchedule'];

    const percentOpen = flowCoefficientSchedule.map(x => x.percentOpen);
    const flowCoefficientScheduleValues = flowCoefficientSchedule.map(x => x.flowCoefficient);

    this.interpolatedCv = +LinearInterpSingleVal(value, percentOpen, flowCoefficientScheduleValues).toFixed(2);
  }
}
